import React, { useRef } from 'react';
import { AudiobookLandingPublic, safePublicUrl, safeRetailers } from './audiobookLandingPublic';
import './css/audiobook-landing.css';

interface Props {
  landing: AudiobookLandingPublic;
  onRetailerClick?: (vendor: string) => void;
  onAuthorClick?: () => void;
}

const ASSETS = 'https://www.alexandersbooks.com/wp-content/plugins/pba-audiobook-retailers/assets/';
const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
  'utm_term', 'utm_id', 'Ad_Name', 'ad_name', 'fbclid'];

export function authorWebsiteHref(base: string, search: string): string | null {
  const href = safePublicUrl(base);
  if (!href) return null;
  const destination = new URL(href);
  const current = new URLSearchParams(search);
  ATTRIBUTION_KEYS.forEach((key) => {
    if (current.has(key)) destination.searchParams.set(key, current.get(key) || '');
  });
  return destination.href;
}

export default function AudiobookLandingView({ landing, onRetailerClick, onAuthorClick }: Props) {
  const recent = useRef(new Map<string, number>());
  const trackChoice = (key: string, callback: (() => void) | undefined, event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented || (event.type === 'auxclick' ? event.button !== 1 : event.button !== 0)) return;
    const now = Date.now();
    if (now - (recent.current.get(key) || 0) < 800) return;
    recent.current.set(key, now);
    callback?.();
  };
  const retailers = safeRetailers(landing);
  const cover = landing.coverUrl && safePublicUrl(landing.coverUrl);
  const author = authorWebsiteHref(landing.authorWebsiteUrl, window.location.search);
  const arrow = <span className="pba-audio-view">View <img src={ASSETS + 'arrow-up-right.svg'} width="16" height="16" alt="" /></span>;

  return <div className="audiobook-landing" lang={landing.language}>
    <main className="pba-audio-landing" data-book-id={landing.editionId}>
      <header className="pba-audio-brand">BookishBoyfriend.com</header>
      <div className="pba-audio-book">
        {cover && <img className="pba-audio-cover" src={cover} width="100" height="150" alt={landing.title} />}
        <div>
          <p className="pba-audio-type"><img src={ASSETS + 'headphones.svg'} width="14" height="14" alt="" /> AUDIOBOOK</p>
          <h1>{landing.title}</h1>
        </div>
      </div>
      <h2>Choose your audiobook store</h2>
      <nav className="pba-audio-stores" aria-label="Audiobook retailers">
        {retailers.map((retailer) => {
          const logo = retailer.logoUrl && safePublicUrl(retailer.logoUrl);
          return <a className="pba-audio-store" key={retailer.vendor}
            data-vendor={retailer.label} data-retailer={retailer.vendor}
            href={safePublicUrl(retailer.url) || undefined} target="_blank"
            rel="noopener noreferrer sponsored"
            aria-label={`View ${landing.title} audiobook on ${retailer.label}`}
            onClick={(event) => trackChoice(retailer.vendor, () => onRetailerClick?.(retailer.vendor), event)}
            onAuxClick={(event) => trackChoice(retailer.vendor, () => onRetailerClick?.(retailer.vendor), event)}>
            {logo ? <img className="pba-audio-logo" src={logo} alt={retailer.label} loading="lazy" />
              : retailer.vendor === 'apple' ? <span className="pba-audio-apple"><img src={ASSETS + 'book-open.svg'} width="23" height="23" alt="" />Apple Books</span>
                : <span>{retailer.label}</span>}
            {arrow}
          </a>;
        })}
        {author && <a className="pba-audio-store pba-audio-author"
          data-retailer="author_website" href={author}
          aria-label={`View ${landing.title} on the author's website`}
          onClick={(event) => trackChoice('author_website', onAuthorClick, event)}
          onAuxClick={(event) => trackChoice('author_website', onAuthorClick, event)}>
          <span className="pba-audio-author-brand"><img src={ASSETS + 'book-open.svg'} width="23" height="23" alt="" />
            <span><strong>Author's website</strong><small>AlexAndersBooks.com</small></span></span>
          {arrow}
        </a>}
      </nav>
      <p className="pba-audio-footer">Sold by your chosen retailer.</p>
    </main>
  </div>;
}

